import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import { signal } from "@preact/signals";
import { REQUESTS } from "../requests.ts";
import type { Piece } from "../shelf.ts";
import { download } from "./download.ts";
import { LetterPaper } from "./letter.tsx";
import styles from "./shelf.module.css";

/** Where a piece's label goes on screen, in pixels (null when it is off the screen). */
export type TagPlace = { x: number; y: number } | null;

/** What the shelf shows over its 3D view: the shelf (shelf.ts) writes it, the screen follows. */
export const shelfView = {
  open: signal(false),
  /** The pieces on the shelf, in order; null while they are loading. */
  pieces: signal<Piece[] | null>(null),
  /** Each piece's label, placed every frame under its pedestal. */
  tags: signal<TagPlace[]>([]),
  selected: signal<Piece | null>(null),
  /** Picked up: the kneading's last step, the step shown, and the strokes by then. */
  inspecting: signal(false),
  lastFrame: signal(0),
  frame: signal(0),
  frameStrokes: signal<number | null>(null),
};

/** What the shelf's screen asks of the shelf. */
export interface ShelfActions {
  close(): void;
  /** Puts the chosen piece back among the others. */
  deselect(): void;
  pickUp(): void;
  putDown(): void;
  showFrame(k: number): void;
  /** Takes the chosen piece off the shelf for good; false if it could not be. */
  remove(): Promise<boolean>;
  /** The piece on its pedestal as a 3D model (GLB). */
  modelOf(piece: Piece): Promise<Blob>;
}

export function mountShelf(root: HTMLElement, actions: ShelfActions): void {
  render(<ShelfScreen actions={actions} />, root);
}

function ShelfScreen({ actions }: { actions: ShelfActions }) {
  if (!shelfView.open.value) return null;
  const pieces = shelfView.pieces.value, selected = shelfView.selected.value;
  const inspecting = shelfView.inspecting.value;
  return (
    <section class={styles.shelf}>
      <header class={`panel ${styles.head}`}>
        <span class={styles.name}>こねこの棚</span>
        <button type="button" class="reset" onClick={() => actions.close()}>もどる</button>
      </header>
      <Tags pieces={pieces ?? []} />
      {pieces?.length === 0 && (
        <p class={styles.empty}>
          まだ何も飾られていません。納品した作品を「棚に飾る」と、ここに並びます。
        </p>
      )}
      {selected && !inspecting && <Detail piece={selected} actions={actions} />}
      {inspecting && <Inspect actions={actions} />}
    </section>
  );
}

/**
 * Museum labels on the shelf's front edge: what was ordered, and what stands for it (the
 * pieces are all about one size, so a hut stands as a scale model).
 */
function Tags({ pieces }: { pieces: Piece[] }) {
  const places = shelfView.tags.value;
  return (
    <div class={styles.tags}>
      {pieces.map((piece, k) => {
        const at = places[k];
        return at && (
          <div key={piece.id} class={styles.tag} style={{ left: `${at.x}px`, top: `${at.y}px` }}>
            <span>依頼物: {piece.request.item}</span>
            <span>展示物: {piece.request.exhibit}</span>
          </div>
        );
      })}
    </div>
  );
}

/** The chosen piece's letter and photo, beside it as it turns. */
function Detail({ piece, actions }: { piece: Piece; actions: ShelfActions }) {
  const [photo, setPhoto] = useState("");
  const [removal, setRemoval] = useState<"not asked" | "asking" | "removing" | "failed">(
    "not asked",
  );
  const [model, setModel] = useState<"not made" | "making" | "failed">("not made");
  useEffect(() => {
    const url = URL.createObjectURL(piece.photo);
    setPhoto(url);
    setRemoval("not asked");
    setModel("not made");
    return () => URL.revokeObjectURL(url);
  }, [piece]);
  const r = piece.request, d = new Date(piece.date);
  // The customer's paper and postscript, from their request: the piece keeps only the letter.
  const sent = REQUESTS.find((q) => q.title === r.title);
  const day = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${
    String(d.getDate()).padStart(2, "0")
  }`;
  return (
    <aside class={`panel ${styles.detail}`}>
      <p class={styles.title}>{r.title}</p>
      {piece.free
        ? (
          <>
            <p class={styles.date}>{day} 仕上げ</p>
            <p class={styles.note}>依頼なしで、自由にこねた作品です。</p>
          </>
        )
        : (
          <>
            <p class={styles.date}>{`${day} 納品　${piece.rank} ランク ${piece.total} 点`}</p>
            <LetterPaper
              body={r.letter}
              from={r.client}
              face={sent?.face}
              paper={sent?.paper}
              postscript={sent?.postscript}
            />
          </>
        )}
      <img class={styles.photo} src={photo} alt={piece.free ? "納品票" : "納品票と評価内訳"} />
      <div class={`row ${styles.actions}`}>
        <button type="button" class="reset" onClick={() => actions.deselect()}>棚にもどす</button>
        <button type="button" class="submit" onClick={() => actions.pickUp()}>
          手に取って見る
        </button>
      </div>
      <div class={styles.links}>
        <button
          type="button"
          class={styles.link}
          disabled={model === "making"}
          onClick={async () => {
            setModel("making");
            try {
              download(
                await actions.modelOf(piece),
                `kneading-needed-${r.title}-${day.replaceAll(".", "")}.glb`,
              );
              setModel("not made");
            } catch {
              setModel("failed");
            }
          }}
        >
          {model === "failed" ? "保存できませんでした" : "3D モデル（GLB）で保存"}
        </button>
        {removal === "not asked" && (
          <button
            type="button"
            class={styles.link}
            onClick={() => setRemoval("asking")}
          >
            棚から下ろす
          </button>
        )}
      </div>
      {removal !== "not asked" && (
        <div class={styles.remove}>
          <p>
            {removal === "failed"
              ? "下ろせませんでした。"
              : "この作品を棚から下ろします。下ろした作品は元にもどせません。"}
          </p>
          <div class={`row ${styles.actions}`}>
            <button type="button" class="reset" onClick={() => setRemoval("not asked")}>
              やめる
            </button>
            <button
              type="button"
              class={styles.danger}
              disabled={removal !== "asking"}
              onClick={async () => {
                setRemoval("removing");
                if (!await actions.remove()) setRemoval("failed");
              }}
            >
              下ろす
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}

/** A piece picked up: turned by dragging, its kneading on a slider. */
function Inspect({ actions }: { actions: ShelfActions }) {
  const last = shelfView.lastFrame.value, strokes = shelfView.frameStrokes.value;
  return (
    <div class={`panel ${styles.inspect}`}>
      <p class={styles.hint}>ドラッグで回す・ホイールやピンチで寄る</p>
      {last > 0 && (
        <label class={styles.timeline}>
          <span>はじめ</span>
          <input
            type="range"
            min="0"
            max={last}
            step="1"
            value={shelfView.frame}
            aria-label="こねた手順"
            onInput={(e) => actions.showFrame(Number(e.currentTarget.value))}
          />
          <span>{strokes === null ? "" : `${strokes}手`}</span>
        </label>
      )}
      <button type="button" class="reset" onClick={() => actions.putDown()}>もどる</button>
    </div>
  );
}
